<template>
  <div class="add-many-candidates g-app-intro-animation g-base-container">
    <eazy-heading :page-name="'candidates'" />

    <section v-if="!parsedCVs.length" class="upload-section">
      <h2 v-if="showUploader">{{ $t('uploadUpTo10CVs') }}</h2>
      <el-upload
        v-if="showUploader"
        ref="upload"
        action="/api/v1/cvs/parse"
        :auto-upload="false"
        multiple
        :limit="maxCVUploadLimit"
        :on-remove="updateFilesArray"
        :on-change="updateFilesArray"
        :on-exceed="showAlertOnCVLimitExceed"
        :file-list="filesForDisplay"
      >
        <el-button slot="trigger" size="small" type="primary">{{ $t('selectCVs') }}</el-button>
        <el-button
          style="margin-left: 10px;"
          size="small"
          type="success"
          :disabled="!filesArray.length"
          @click="submitUploadCVs"
          >{{ $t('upload') }}</el-button
        >
        <div slot="tip" class="el-upload__tip">{{ $t('pdfFilesOnly') }}</div>
      </el-upload>
    </section>

    <div v-if="showProgressBar && !parsingCVsSuccess">
      <h2>{{ $t('processing') }}</h2>
      <el-progress :percentage="parsingCVsPercentage"></el-progress>
    </div>

    <div v-if="parsedCVs.length">
      <h2 class="g-text-success g-text-success--no-inputs">
        1. {{ $t('successfullyProcessedCVs') }} &#10003;
      </h2>
      <el-progress :percentage="100" status="success"></el-progress>
    </div>

    <section v-if="parsedCVs.length" class="create-candidates-section">
      <h2
        :class="{
          'g-text-success g-text-success--no-inputs': createdCandidates.length === parsedCVs.length,
        }"
      >
        2. {{ $t('createdCandidatesTitle') }}
        <strong :class="{ 'g-text-warn': createdCandidates.length < parsedCVs.length }">
          {{ createdCandidates.length }}/{{ parsedCVs.length }}
        </strong>
        <span v-if="createdCandidates.length === parsedCVs.length">&#10003;</span>
      </h2>
      <p>
        {{ $t('createdCandidatesText') }}
      </p>

      <ul class="parsed-cvs-list">
        <li
          v-for="(cv, idx) in parsedCVs"
          :key="idx"
          :class="[
            'parsed-cvs-list__item',
            { 'parsed-cvs-list__item--uploaded': cv.candidateUploaded },
          ]"
          @click="goToCandidate(cv)"
        >
          <span class="parsed-cvs-list__item-content">
            <img :src="getImageForParsedCVListItem(cv)" />
            <span class="parsed-cvs-list__item-content-text">{{
              getTextForParsedCVListItem(cv)
            }}</span>
          </span>
          <i class="parsed-cvs-list__item-icon el-icon-edit-outline"></i>
          <i v-if="cv.candidateUploaded" class="parsed-cvs-list__item-icon">&#10003;</i>
          <div v-if="cv.candidateUploaded" class="parsed-cvs-list__item-uploaded-overlay">
            {{ $t('candidateCreated') }}
            <i class="parsed-cvs-list__item-icon parsed-cvs-list__item-icon--hide">&#10003;</i>
          </div>
        </li>
      </ul>
    </section>

    <section v-if="candidatesCreatedForAllParsedCVs" class="success-section">
      <p>{{ $t('youHaveUploadedAllCandidatesBasedOnParsedCVs') }}</p>
      <strong>{{ $t('goTo') }}:</strong>
      <button class="g-btn-secondary" @click="exitAndGoToPath('/client-candidates')">
        {{ $t('candidates') }}
      </button>
      <button class="g-btn-secondary" @click="exitAndGoToPath('/client-tasks')">
        {{ $t('dashboard') }}
      </button>
    </section>
  </div>
</template>

<script>
import EazyHeading from '@/components/EazyHeading.vue';
import requestAPI from '@/logic/requestBeApi';
import router from '@/router';
import store from '@/store';
import config from '@/config';

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const getBase64StringWithoutTitle = (string) => {
  return string.replace('data:application/pdf;base64,', '');
};

export default {
  name: 'AddManyCandidates',

  components: {
    EazyHeading,
  },

  data() {
    return {
      router,
      parsingCVs: false,
      parsingCVsPercentage: 0,
      parsingCVsSuccess: false,
      filesArray: [],
      filesForDisplay: [],
      maxCVUploadLimit: 10,
      placeholderAvatar: require('@/assets/default-avatar.png'),
    };
  },

  computed: {
    roleType() {
      return localStorage.getItem('roleType');
    },

    showProgressBar() {
      return this.parsingCVs || this.parsingCVsSuccess;
    },

    showUploader() {
      return !this.parsingCVs && !this.parsingCVsSuccess;
    },

    progressBarStatus() {
      return this.parsingCVsSuccess ? 'success' : 'normal';
    },

    parsedCVs() {
      return store.state.multipleCVs.parsedCVs;
    },

    createdCandidates() {
      return store.state.multipleCVs.parsedCVs.filter((cv) => {
        return cv.candidateUploaded;
      });
    },

    candidatesCreatedForAllParsedCVs() {
      return (
        (this.createdCandidates.length > 0) &
        (this.createdCandidates.length === this.parsedCVs.length)
      );
    },
  },

  methods: {
    updateFilesArray(file, filesList) {
      this.filesArray = filesList
        .filter((f) => {
          const fileSizeIsOK = f.size <= config.MAX_FILE_SIZE;
          const fileTypeIsOK = f.raw?.type === config.SUPPORTED_TYPE_FOR_CVS;

          return fileSizeIsOK && fileTypeIsOK;
        })
        .map((f) => {
          return f.raw;
        });

      this.filesForDisplay = filesList.filter((f) => {
        const fileSizeIsOK = f.size <= config.MAX_FILE_SIZE;
        const fileTypeIsOK = f.raw?.type === config.SUPPORTED_TYPE_FOR_CVS;

        return fileSizeIsOK && fileTypeIsOK;
      });

      this.notifyAboutWrongFile(file);
    },

    notifyAboutWrongFile(file) {
      const fileSizeIsOK = file.size <= config.MAX_FILE_SIZE;
      const fileTypeIsOK = file.raw.type === config.SUPPORTED_TYPE_FOR_CVS;

      setTimeout(() => {
        if (!fileSizeIsOK) {
          const message = `<b>${file.name}</b> ${this.$t('CVFileWrongSize')}`;
          this.displayWarningNotification(message);
        } else if (!fileTypeIsOK) {
          const message = `<b>${file.name}</b> ${this.$t('CVFileWrongType')}`;
          this.displayWarningNotification(message);
        }
      }, 1);
    },

    displayWarningNotification(message) {
      this.$notify.warning({
        title: this.$t('warning'),
        dangerouslyUseHTMLString: true,
        message,
        duration: 11000,
      });
    },

    async submitUploadCVs() {
      const filesConvertedToBase64 = [];

      if (!this.filesArray.length) return;

      const promisesOfFilesConvertedToBase64 = await this.filesArray.map(async (file) => {
        const convertedFile = await toBase64(file);
        return convertedFile;
      });

      await Promise.all(promisesOfFilesConvertedToBase64).then((values) => {
        values.forEach((value) => {
          filesConvertedToBase64.push({
            file: getBase64StringWithoutTitle(value),
          });
        });
      });

      this.uploadCVsAndHandleResponse(filesConvertedToBase64);
    },

    async uploadCVsAndHandleResponse(files) {
      this.parsingCVs = true;
      this.runPercentageCounter(files.length);

      const resp = await requestAPI({
        url: `/api/v1/cvs/parse`,
        method: 'POST',
        payload: {
          files,
        },
      });

      if (resp.ok) {
        const data = await resp.json();
        const parsedCVs = this.getParsedCVsWithBaseFiles(data.cvs, files);
        store.commit('multipleCVs/setParsedCVs', parsedCVs);
        this.parsingCVsSuccess = true;
      } else {
        this.$message({
          showClose: true,
          message: this.$t('CVsParsingError'),
          type: 'error',
          duration: 8000,
        });
      }

      this.parsingCVs = false;
    },

    getParsedCVsWithBaseFiles(parsedCVs, files) {
      return parsedCVs.map((parsedCV, idx) => {
        parsedCV.cv_file = files[idx].file;
        return parsedCV;
      });
    },

    runPercentageCounter(filesNumber) {
      const intervalBaseInMiliseconds = 10;
      const intervalInMiliseconds = intervalBaseInMiliseconds * filesNumber;

      setInterval(() => {
        if (this.parsingCVsPercentage < 99) {
          this.parsingCVsPercentage += 1;
        }

        if (this.parsingCVsSuccess) {
          this.parsingCVsPercentage = 100;
        }
      }, intervalInMiliseconds);
    },

    goToCandidate(cv) {
      if (this.roleType !== 'assessorPlus') {
        if (cv.candidateUploaded) return;
        store.commit('multipleCVs/setCurrentlySelectedCV', cv);

        router.push({ path: '/client-add-candidate', query: { fromMultipleUpload: 'true' } });
      } else {
        if (cv.candidateUploaded) return;
      }
      store.commit('multipleCVs/setCurrentlySelectedCV', cv);
      router.push({ path: '/assessor-add-candidate', query: { fromMultipleUpload: 'true' } });
    },

    getImageForParsedCVListItem(cv) {
      if (cv.profile_image) {
        return `data:image;base64,${cv.profile_image}`;
      } else {
        return this.placeholderAvatar;
      }
    },

    getTextForParsedCVListItem(cv) {
      let text = '';

      if (cv.name || cv.surname) {
        text = `${cv.name} ${cv.surname}`;
      } else if (cv.email) {
        text = cv.email;
      } else {
        text = this.$t('candidate');
      }

      return text;
    },

    exitAndGoToPath(path) {
      store.commit('multipleCVs/setParsedCVs', []);
      const newPath =
        this.roleType === 'assessorPlus' ? `/assessor${path.replace('/client', '')}` : path;
      router.push(newPath);
    },

    showAlertOnCVLimitExceed() {
      this.$message.warning({
        message: this.$t('youCanUploadOnlyUpTo10Cvs'),
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.add-many-candidates {
  .upload-section {
    button {
      min-width: 100px;
    }
  }

  .create-candidates-section {
    margin-top: 50px;
  }

  .el-progress.el-progress--line.is-success {
    .el-progress-bar {
      padding-right: 17px;
    }

    .el-progress__text {
      margin-left: 40px;
    }
  }

  .parsed-cvs-list {
    margin-top: 30px;
    padding: 0;
  }

  .parsed-cvs-list__item {
    list-style: none;
    padding: 5px;
    border-top: 1px solid $borderColor;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;

    .parsed-cvs-list__item-content {
      display: flex;
      align-items: center;

      img {
        width: 38px;
        border-radius: 50%;
        height: 38px;
        margin-right: 10px;
        object-fit: cover;
      }
    }

    &.parsed-cvs-list__item--uploaded {
      color: $colorSuccess;

      &:hover {
        .parsed-cvs-list__item-uploaded-overlay {
          opacity: 1;
          padding: 10px 5px 5px 53px;
        }

        .parsed-cvs-list__item-content-text {
          display: none;
        }
      }
    }

    &:last-child {
      border-bottom: 1px solid $borderColor;
    }

    &:hover:not(.parsed-cvs-list__item--uploaded) {
      color: $elUIPrimaryHovered;
      cursor: pointer;

      .el-icon-edit-outline {
        display: inline-block;
      }
    }
  }

  .parsed-cvs-list__item-icon {
    font-size: 18px;

    &.parsed-cvs-list__item-icon--hide {
      opacity: 0;
    }

    &.el-icon-edit-outline {
      display: none;
    }
  }

  .parsed-cvs-list__item-uploaded-overlay {
    top: 0;
    left: 0;
    padding: 0 5px 5px 53px;
    height: 49px;
    overflow: hidden;
    position: absolute;
    transition: 0.25s all;
    opacity: 0;
  }

  .success-section {
    margin-top: 50px;

    .g-btn-secondary {
      margin-left: 12.5px;
    }
  }
}
</style>
